// `field/forms/` API service (axios) — see
// docs/developer-guides/FIELD_FRONTEND_GUIDE.md §3 (authoring endpoints).
//
// Replaces the localStorage-backed form store. Follows the existing service
// pattern (see src/services/cmTables.ts): thin functions over the shared `api`
// axios instance; React Query hooks live in src/api/fieldForms.ts.

import { AxiosResponse } from "axios";
import { api } from ".";
import { CreateFormPayload, FieldForm, ListFormsParams, UpdateFormPayload } from "../types/fieldForm";

/** DRF-style paginated list envelope. */
export interface PaginatedResponse<T> {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results: T[];
}

const BASE = "v2/field/forms";

/**
 * The backend wraps single-object responses in `{ message, data }` and returns
 * `table_slug` / `last_updated_at`. Unwrap the envelope and normalise those onto
 * the `FieldForm` shape (`table` / `updated_at`) the app expects.
 */
function unwrapForm(body: unknown): FieldForm {
  const envelope = body as { data?: unknown } | null;
  const hasEnvelope = !!envelope && typeof envelope === "object" && "data" in envelope && !!envelope.data;
  const raw = (hasEnvelope ? envelope.data : body) as Record<string, unknown>;
  return {
    ...(raw as unknown as FieldForm),
    table: (raw.table ?? raw.table_slug) as string,
    updated_at: (raw.updated_at ?? raw.last_updated_at) as string | undefined
  };
}

/**
 * List forms. Filter by `?table=<slug>`, `?status=`, or `?form_group_id=` (to
 * enumerate versions of one logical form).
 * Endpoint: GET /api/v2/field/forms/
 */
export const listForms = async (
  params?: ListFormsParams & { form_group_id?: string }
): Promise<AxiosResponse<PaginatedResponse<FieldForm>>> => {
  const res = await api.get(`${BASE}/`, {
    params: {
      ...(params?.table ? { table: params.table } : {}),
      ...(params?.status ? { status: params.status } : {}),
      ...(params?.form_group_id ? { form_group_id: params.form_group_id } : {})
    }
  });
  // Normalise each item the same way single-form responses are normalised —
  // the list endpoint uses `table_slug` / `last_updated_at` field names.
  if (res.data?.results) {
    res.data.results = res.data.results.map((item: unknown) => unwrapForm(item));
  }
  return res;
};

/**
 * Retrieve one form version.
 * Endpoint: GET /api/v2/field/forms/<uuid>/
 */
export const getForm = async (uuid: string): Promise<AxiosResponse<FieldForm>> => {
  const res = await api.get(`${BASE}/${uuid}/`);
  return { ...res, data: unwrapForm(res.data) };
};

/**
 * Create a draft (optionally a new version of an existing `form_group_id`).
 * Endpoint: POST /api/v2/field/forms/
 */
export const createForm = async (payload: CreateFormPayload): Promise<AxiosResponse<FieldForm>> => {
  const res = await api.post(`${BASE}/`, payload);
  return { ...res, data: unwrapForm(res.data) };
};

/**
 * Edit a draft. `schema` is a FULL replacement, not a merge. 409 if published.
 * Endpoint: PATCH /api/v2/field/forms/<uuid>/
 */
export const updateForm = async (uuid: string, payload: UpdateFormPayload): Promise<AxiosResponse<FieldForm>> => {
  const res = await api.patch(`${BASE}/${uuid}/`, payload);
  return { ...res, data: unwrapForm(res.data) };
};

/**
 * Validate + freeze a draft (empty body). Runs the publish invariants.
 * Endpoint: POST /api/v2/field/forms/<uuid>/publish/
 */
export const publishForm = async (uuid: string): Promise<AxiosResponse<FieldForm>> => {
  const res = await api.post(`${BASE}/${uuid}/publish/`, {});
  return { ...res, data: unwrapForm(res.data) };
};

/**
 * Archive (or hard-delete a draft without submissions).
 * Endpoint: DELETE /api/v2/field/forms/<uuid>/
 */
export const deleteForm = async (uuid: string): Promise<AxiosResponse<void>> => {
  return api.delete(`${BASE}/${uuid}/`);
};

/**
 * Published forms for a table (drives `mode:"new"` relation pickers + EntityForm
 * schema-field capture). The endpoint returns the array in an envelope
 * `{ data: FieldForm[] }` (each item uses `table_slug` / `last_updated_at`), so
 * normalise it to the `{ results }` shape the app's hooks expect, unwrapping
 * each item onto the `FieldForm` field names.
 * Endpoint: GET /api/v2/field/tables/<slug>/forms/
 */
export const listPublishedFormsForTable = async (tableSlug: string): Promise<AxiosResponse<PaginatedResponse<FieldForm>>> => {
  const res = await api.get(`v2/field/tables/${tableSlug}/forms/`);
  const body = res.data as { data?: unknown[]; results?: unknown[] } | unknown[];
  const rawList: unknown[] = Array.isArray(body)
    ? body
    : Array.isArray((body as any)?.data)
      ? (body as any).data
      : Array.isArray((body as any)?.results)
        ? (body as any).results
        : [];
  return { ...res, data: { results: rawList.map((item) => unwrapForm(item)) } };
};
